import React, { FC, Fragment, ReactNode, useMemo } from "react";
import { LayoutChangeEvent } from "react-native";
import { View } from "@lookiero/aurora";
import { ProductPreviewWithDiscount } from "../../../../domain/projections/ProductPreviewWithDiscount";
import { useExtendedScreen } from "../../hooks/useExtendedScreen";
import { cdnImageUrl } from "../../shared/util/cdnImageUrl";
import { getCurrentScreenSize } from "../../shared/util/screen";
import { ProductPreviewCard } from "../productPreviewCard/ProductPreviewCard";
import { style as productPreviewListStyle, getImageWidthInPixels } from "./ProductPreviewList.style";

interface ProductPreviewListProps {
  readonly products: ProductPreviewWithDiscount[] | undefined;
  readonly featuredProductFrequency: number;
  readonly banner?: (singleColumn: boolean) => ReactNode;
  readonly onItemPress?: (product: ProductPreviewWithDiscount, index: number) => void;
  readonly onLayoutProductList: (event: LayoutChangeEvent) => void;
}

const ProductPreviewList: FC<ProductPreviewListProps> = ({
  products = [],
  featuredProductFrequency,
  banner,
  onItemPress = () => void 0,
  onLayoutProductList,
}) => {
  const screen = useExtendedScreen();
  const breakpoint = screen.XL ? "XL" : getCurrentScreenSize(screen);
  const showDesktopLayout = screen.XL;

  const style = useMemo(() => productPreviewListStyle(), []);

  const showBanner = (index: number) => {
    const initialPosition = Math.floor(products.length / 2);
    const position = initialPosition % 2 === 0 ? initialPosition : initialPosition + 1;

    return index === position;
  };

  return (
    <View style={[style.layout, showDesktopLayout ? undefined : style.space8mall]} onLayout={onLayoutProductList}>
      {products.map((product, index) => {
        const isFeatured = index % featuredProductFrequency === 0;
        const imageUrl = isFeatured ? product.images.featured : product.images.standard;
        const imageWidthInPixels = getImageWidthInPixels(isFeatured, breakpoint);

        return (
          <Fragment key={product.id}>
            {banner && showBanner(index) ? (
              <View style={showDesktopLayout ? style.halfWidthBanner : style.fullWidthBanner}>
                {banner(showDesktopLayout === false)}
              </View>
            ) : (
              <></>
            )}
            <View
              testID={`category-item-${product.id}`}
              style={[
                style.item,
                showDesktopLayout ? undefined : isFeatured ? style.fullWidthItem : style.halfWidthItem,
              ]}
            >
              <ProductPreviewCard
                detailImageUrl={cdnImageUrl({ imageUrl: product.images.detail, width: imageWidthInPixels })}
                imageUrl={cdnImageUrl({ imageUrl, width: imageWidthInPixels })}
                product={product}
                onItemPress={(itemPressed) => {
                  onItemPress(itemPressed, index);
                }}
              />
            </View>
          </Fragment>
        );
      })}
    </View>
  );
};

export { ProductPreviewList };
